import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Modal } from '../../components/ui/modal';
import TransactionReceipt from '../../components/transactions/TransactionReceipt';
import OperationLayout from '../../components/operations/OperationLayout';
import ProviderCard from '../../components/operations/ProviderCard';
import { PROVIDERS } from '../../lib/constants';
import { Method, InsurancePlan, operationsService } from '../../services/api/operationsService';
import { addTransaction } from '../../store/slices/transactionsSlice';
import { printService } from '../../services/printService';
import { Transaction } from '../../types';

// ─── constants ────────────────────────────────────────────────────────────────
const ACCENT = '#f59e0b';

const fmt = (n: number) =>
  new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP', minimumFractionDigits: 2 }).format(n);

const VEHICLE_TYPES = [
  { id: '1', label: 'Particular' },
  { id: '2', label: 'Comercial' },
  { id: '3', label: 'Motocicleta' },
  { id: '4', label: 'Camión / Pesado' },
];

const STEPS = [
  { label: 'Compañía' },
  { label: 'Plan' },
  { label: 'Teléfono' },
  { label: 'Documento' },
  { label: 'Vehículo' },
  { label: 'Confirmar' },
];

// ─── component ────────────────────────────────────────────────────────────────
const SegurosPageSteps: React.FC = () => {
  const dispatch   = useAppDispatch();
  const { wallet } = useAppSelector((state) => state.wallet as any);
  const { insuranceMethods } = useAppSelector((state) => state.operations as any);
  const fetchedRef = useRef(false);

  // ── step state ─────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(1);

  // ── form fields ────────────────────────────────────────────────────────────
  const [selectedProvider, setSelectedProvider] = useState('');
  const [selectedPlan,     setSelectedPlan]     = useState<InsurancePlan | null>(null);
  const [phoneNumber,      setPhoneNumber]      = useState('');
  const [documentId,       setDocumentId]       = useState('');
  const [chassisNumber,    setChassisNumber]    = useState('');
  const [vehicleType,      setVehicleType]      = useState('');

  // ── plans fetched from API ─────────────────────────────────────────────────
  const [plans,        setPlans]        = useState<InsurancePlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);

  // ── processing ─────────────────────────────────────────────────────────────
  const [isProcessing,      setIsProcessing]      = useState(false);
  const [error,             setError]             = useState<string | null>(null);
  const [success,           setSuccess]           = useState<string | null>(null);
  const [showReceiptModal,  setShowReceiptModal]  = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  // ── load operations data once ──────────────────────────────────────────────
  useEffect(() => {
    if (!fetchedRef.current && (!insuranceMethods || insuranceMethods.length === 0)) {
      fetchedRef.current = true;
      dispatch(fetchOperationsData());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const providerList: Method[] =
    insuranceMethods && insuranceMethods.length > 0 ? insuranceMethods : PROVIDERS.SEGUROS;

  // ── navigation ─────────────────────────────────────────────────────────────
  const goBack = () => { if (currentStep > 1) setCurrentStep(s => s - 1); setError(null); };

  // ── Step 1: select provider and fetch plans ────────────────────────────────
  const handleProviderSelect = async (id: string) => {
    setSelectedProvider(id);
    setSelectedPlan(null);
    setPlans([]);
    setError(null);
    setLoadingPlans(true);
    setCurrentStep(2);
    try {
      const res = await operationsService.fetchInsurances(id);
      if (!res.success) throw new Error(res.message || 'Error al consultar planes');
      const raw: any[] = Array.isArray(res.data) ? res.data : [];
      console.log('[Seguros] raw plans from API:', JSON.stringify(raw));

      // Helper: busca el primer valor string no vacío entre las claves dadas
      const pick = (obj: any, ...keys: string[]): string => {
        for (const k of keys) {
          const v = obj[k];
          if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
        }
        return '';
      };

      // Helper: primer valor string no vacío de cualquier campo del objeto (fallback último recurso)
      const firstString = (obj: any, exclude: string[] = []): string => {
        for (const k of Object.keys(obj)) {
          if (exclude.includes(k)) continue;
          const v = obj[k];
          if (typeof v === 'string' && v.trim() !== '') return v.trim();
        }
        return '';
      };

      const normalized: InsurancePlan[] = raw.map((p: any) => {
        // Campos confirmados del API externo (InsuranceSection.php):
        //   descrip → nombre del plan
        //   precio  → precio
        //   vigencia → vigencia
        //   tipo_veh → tipo de vehículo
        const name = pick(p,
          'descrip',                        // ← campo real del API externo
          'name', 'nombre', 'plan', 'plan_name', 'planName',
          'description', 'descripcion', 'desc',
          'tipo', 'tipo_seguro', 'tipoSeguro',
          'product', 'producto', 'title', 'titulo',
          'insurance_name', 'insuranceName', 'seguro',
        ) || firstString(p, ['id', 'price', 'precio', 'monto', 'vigencia', 'vigency', 'tipo_veh']);

        return {
          id:           pick(p, 'id', 'planId', 'plan_id', 'cod', 'codigo', 'code'),
          name,
          description:  pick(p, 'description', 'descripcion', 'desc', 'coverage', 'cobertura'),
          price:        Number(pick(p, 'precio', 'price', 'amount', 'monto', 'costo', 'valor')) || 0,
          vigency:      pick(p, 'vigencia', 'vigency', 'days', 'dias', 'validity') || '365',
          vehicle_type: pick(p, 'tipo_veh', 'vehicle_type', 'vehicleType', 'tipo_vehiculo'),
        };
      });
      if (normalized.length === 0) {
        setError('No hay planes disponibles para esta compañía');
      } else {
        setPlans(normalized);
      }
    } catch (err: any) {
      setError(err.message || 'Error al consultar los planes de seguro');
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  // ── Step 2: select plan ────────────────────────────────────────────────────
  const handlePlanSelect = (plan: InsurancePlan) => {
    setSelectedPlan(plan);
    setError(null);
    setCurrentStep(3);
  };

  // ── Step 3: phone ──────────────────────────────────────────────────────────
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '');
    if (v.length <= 10) setPhoneNumber(v);
  };
  const handlePhoneSubmit = () => {
    if (!phoneNumber || phoneNumber.length < 10) { setError('Ingrese un número de teléfono válido de 10 dígitos'); return; }
    setError(null); setCurrentStep(4);
  };

  // ── Step 4: document (cédula) ─────────────────────────────────────────────
  const handleDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '');
    if (v.length <= 11) setDocumentId(v);
  };
  const handleDocSubmit = () => {
    if (!documentId || documentId.length !== 11) { setError('Ingrese un número de cédula válido (11 dígitos)'); return; }
    setError(null); setCurrentStep(5);
  };

  // ── Step 5: vehicle (chassis + type) ──────────────────────────────────────
  const handleChassisChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setChassisNumber(e.target.value.toUpperCase());

  const handleVehicleSubmit = () => {
    if (!chassisNumber) { setError('Ingrese el número de chasis del vehículo'); return; }
    if (!vehicleType)   { setError('Seleccione el tipo de vehículo'); return; }
    setError(null); setCurrentStep(6);
  };

  // ── Step 6: confirm + submit ───────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!selectedPlan || !selectedProvider || !phoneNumber || !documentId || !chassisNumber || !vehicleType) {
      setError('Complete todos los campos para continuar'); return;
    }
    const walletBalance = wallet?.balance ?? 0;
    if (walletBalance < selectedPlan.price) { setError('Saldo insuficiente para contratar este seguro'); return; }

    setIsProcessing(true); setError(null);
    try {
      const res = await operationsService.sendInsurance(
        selectedPlan.price,
        phoneNumber,
        selectedProvider,
        selectedPlan.name,
        selectedPlan.vigency || '365',
        vehicleType,
        documentId,
        chassisNumber
      );
      if (!res.success) throw new Error(res.message || 'Error al contratar el seguro');

      const providerName = providerList.find(p => p.id === selectedProvider)?.name || selectedProvider;
      const transaction: any = {
        id:       res.data?.xid || res.data?.transactionId || `ins-${Date.now()}`,
        xid:      res.data?.xid || res.data?.transactionId || undefined,
        type:     'SEGURO' as const,
        amount:   selectedPlan.price,
        provider: providerName,
        date:     new Date().toISOString(),
        status:   'COMPLETED' as const,
        reference: Math.random().toString(36).substring(2, 10).toUpperCase(),
        url:      res.data?.url || null,
        details: {
          phoneNumber,
          document:   documentId,
          chassis:    chassisNumber,
          vehicleType,
          planName:   selectedPlan.name,
          vigency:    selectedPlan.vigency || '365',
          price:      selectedPlan.price,
        },
      };
      dispatch(addTransaction(transaction));
      setSuccess(`Seguro "${selectedPlan.name}" contratado con éxito`);
      setCurrentTransaction(transaction);
      setShowReceiptModal(true);
      // reset
      setPhoneNumber(''); setDocumentId(''); setChassisNumber('');
      setSelectedProvider(''); setSelectedPlan(null); setVehicleType('');
      setPlans([]); setCurrentStep(1);
    } catch (err: any) {
      setError(err.message || 'Error al procesar la contratación. Intente nuevamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  // ── render steps ───────────────────────────────────────────────────────────
  const renderStepContent = () => {
    switch (currentStep) {

      // ── 1. Compañía ──────────────────────────────────────────────────────
      case 1:
        return (
          <div className="space-y-5 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione la compañía</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {providerList.map((p: Method) => (
                <ProviderCard key={p.id} id={p.id} name={p.name} logo={p.logo}
                  onClick={handleProviderSelect} accentColor={ACCENT} />
              ))}
            </div>
          </div>
        );

      // ── 2. Plan ──────────────────────────────────────────────────────────
      case 2:
        return (
          <div className="space-y-5 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Seleccione el plan de seguro</p>

            {loadingPlans ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-500">
                <svg className="animate-spin w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span className="text-sm font-medium">Consultando planes disponibles…</span>
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">
                <p>No hay planes disponibles para esta compañía.</p>
                <Button variant="outline" size="lg" className="mt-4" onClick={goBack}>← Volver</Button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {plans.map((plan) => (
                    <button key={plan.id} type="button" onClick={() => handlePlanSelect(plan)}
                      className="w-full flex items-center justify-between p-4 rounded-2xl border-2 text-left transition-all duration-150 hover:border-amber-400 hover:bg-amber-50 active:scale-95"
                      style={{ borderColor: '#e2e8f0', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      <div className="flex-1 min-w-0 pr-4">
                        <p className="font-bold text-slate-800 truncate">{plan.name || plan.id || `Plan ${plan.id}`}</p>
                        {plan.description && plan.description !== plan.name && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{plan.description}</p>
                        )}
                        {plan.vigency && (
                          <p className="text-xs text-amber-600 mt-1 font-medium">Vigencia: {plan.vigency}</p>
                        )}
                      </div>
                      <span className="text-lg font-black shrink-0" style={{ color: ACCENT }}>{fmt(plan.price)}</span>
                    </button>
                  ))}
                </div>
                <Button variant="outline" size="lg" onClick={goBack}>← Atrás</Button>
              </>
            )}
          </div>
        );

      // ── 3. Teléfono ──────────────────────────────────────────────────────
      case 3:
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Teléfono del asegurado</p>
            <div className="max-w-sm mx-auto">
              <Input label="Número de teléfono"
                value={phoneNumber} onChange={handlePhoneChange}
                placeholder="8091234567" fullWidth size="touch" type="tel"
                leftAddon="+1" useCustomKeyboard keyboardType="numeric"
              />
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="lg" onClick={goBack}>← Atrás</Button>
              <Button size="lg" onClick={handlePhoneSubmit} disabled={!phoneNumber || phoneNumber.length < 10}>
                Continuar →
              </Button>
            </div>
          </div>
        );

      // ── 4. Documento ─────────────────────────────────────────────────────
      case 4:
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Documento de identidad</p>
            <div className="max-w-sm mx-auto">
              <Input label="Cédula (11 dígitos)"
                value={documentId} onChange={handleDocChange}
                placeholder="00112345678" fullWidth size="touch"
                useCustomKeyboard keyboardType="numeric"
              />
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="outline" size="lg" onClick={goBack}>← Atrás</Button>
              <Button size="lg" onClick={handleDocSubmit} disabled={!documentId || documentId.length !== 11}>
                Continuar →
              </Button>
            </div>
          </div>
        );

      // ── 5. Vehículo ──────────────────────────────────────────────────────
      case 5:
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Datos del vehículo</p>

            {/* Número de chasis */}
            <div className="max-w-sm mx-auto">
              <Input label="Número de chasis / VIN"
                value={chassisNumber} onChange={handleChassisChange}
                placeholder="1HGBH41JXMN109186" fullWidth size="touch"
                useCustomKeyboard keyboardType="alphanumeric"
              />
            </div>

            {/* Tipo de vehículo */}
            <div>
              <p className="text-sm font-medium text-slate-600 mb-3">Tipo de vehículo</p>
              <div className="grid grid-cols-2 gap-3">
                {VEHICLE_TYPES.map(vt => (
                  <button key={vt.id} type="button" onClick={() => setVehicleType(vt.id)}
                    className="p-4 rounded-2xl border-2 text-center font-semibold text-sm transition-all duration-150 active:scale-95"
                    style={{
                      borderColor: vehicleType === vt.id ? ACCENT : '#e2e8f0',
                      background:  vehicleType === vt.id ? `${ACCENT}18` : '#fff',
                      color:       vehicleType === vt.id ? ACCENT : '#475569',
                    }}>
                    {vt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" size="lg" onClick={goBack}>← Atrás</Button>
              <Button size="lg" onClick={handleVehicleSubmit} disabled={!chassisNumber || !vehicleType}>
                Continuar →
              </Button>
            </div>
          </div>
        );

      // ── 6. Confirmar ─────────────────────────────────────────────────────
      case 6: {
        const providerName  = providerList.find(p => p.id === selectedProvider)?.name || selectedProvider;
        const vehicleLabel  = VEHICLE_TYPES.find(v => v.id === vehicleType)?.label || vehicleType;
        return (
          <div className="space-y-6 animate-fadeIn">
            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Confirmar contratación</p>
            <div className="rounded-2xl border border-slate-100 divide-y divide-slate-100 overflow-hidden"
              style={{ background: '#f8fafc' }}>
              {[
                { label: 'Compañía',       value: providerName },
                { label: 'Plan',           value: selectedPlan?.name || selectedPlan?.id || '' },
                { label: 'Vigencia',       value: selectedPlan?.vigency || '—' },
                { label: 'Teléfono',       value: `+1 ${phoneNumber}` },
                { label: 'Documento',      value: documentId },
                { label: 'Chasis / VIN',   value: chassisNumber },
                { label: 'Tipo vehículo',  value: vehicleLabel },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center px-5 py-3">
                  <span className="text-sm text-slate-500 font-medium">{label}</span>
                  <span className="text-sm font-bold text-slate-800">{value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center px-5 py-4" style={{ background: `${ACCENT}15` }}>
                <span className="text-sm text-slate-500 font-medium">Monto</span>
                <span className="text-2xl font-black" style={{ color: ACCENT }}>{fmt(selectedPlan?.price || 0)}</span>
              </div>
            </div>
            <Button size="touch" fullWidth isLoading={isProcessing} onClick={handleSubmit}>
              {isProcessing ? 'Procesando...' : '✓ Confirmar Contratación'}
            </Button>
            <Button variant="outline" size="lg" onClick={goBack} disabled={isProcessing}>← Atrás</Button>
          </div>
        );
      }

      default: return null;
    }
  };

  return (
    <>
      <OperationLayout
        title="Seguros"
        description="Contrate seguros de vehículos"
        steps={STEPS} currentStep={currentStep}
        error={error} success={success} accentColor={ACCENT}
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
          </svg>
        }
      >
        {renderStepContent()}
      </OperationLayout>

      <Modal isOpen={showReceiptModal} onClose={() => setShowReceiptModal(false)}
        title="Resumen de Transacción" size="md">
        {currentTransaction && (
          <TransactionReceipt
            transaction={currentTransaction}
            onClose={() => setShowReceiptModal(false)}
            onPrint={() => { if (currentTransaction) printService.printTicket(currentTransaction); }}
          />
        )}
      </Modal>
    </>
  );
};

export default SegurosPageSteps;
